import * as React from 'react'

import { Anchor } from '@/components/ui/Anchor'
import { cn } from '@/lib/utils'

export interface ProductFlowFooterProps {
  legalLinks: { label: string; href: string }[]
  /** Defaults to the current year and Cai's legal entity name if omitted. */
  copyright?: string
  /** Fine-print compliance copy, e.g. licensing disclosure. Accepts multiple paragraphs. Optional. */
  note?: React.ReactNode
  className?: string
}

/**
 * Stripped-back footer for product flows (funnels, applications) — only the
 * copyright/legal row and optional fine print. No logo, tagline, or link
 * columns or edge lines; SiteFooter covers those for the marketing site.
 */
export function ProductFlowFooter({ legalLinks, copyright, note, className }: ProductFlowFooterProps) {
  const copyrightText = copyright ?? `© ${new Date().getFullYear()} Cai Labs, Inc. All rights reserved.`
  return (
    <footer data-site-footer className={cn('bg-ink-900 px-5.5 py-8 text-sm text-white/70', className)}>
      <div className="mx-auto max-w-(--maxw)">
        <div className="flex flex-wrap justify-between gap-x-6 gap-y-2 font-mono text-xs opacity-80">
          <span>{copyrightText}</span>
          <span className="flex gap-6">
            {legalLinks.map((link) => (
              <Anchor key={link.label} href={link.href} className="text-inherit no-underline opacity-85">
                {link.label}
              </Anchor>
            ))}
          </span>
        </div>
        {note ? (
          <div className="[&_a:hover]:text-brand-600 mt-8 flex flex-col gap-4 font-mono text-xs leading-normal text-white/60 [&_a]:text-white [&_a]:underline [&_a]:underline-offset-2">
            {note}
          </div>
        ) : null}
      </div>
    </footer>
  )
}
